import { resolveMediaUrl } from './api';

const PRINT_FRAME_ID = 'pizas-receipt-print-frame';

function esc(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function money(n) {
  return `$ ${Number(n || 0).toFixed(2)}`;
}

function buildReceiptHtml(r) {
  const imgUrl = r.header_image_url ? resolveMediaUrl(r.header_image_url) : '';
  const headerHtml = (r.header_lines || [])
    .map((ln) => `<div class="center bold">${esc(ln)}</div>`)
    .join('');
  const linesHtml = (r.lines || [])
    .map(
      (ln) => `
      <tr>
        <td class="desc">${esc(ln.description)}</td>
        <td class="num">${ln.qty} ${esc(ln.unit_label || 'Pza')}</td>
        <td class="num">${money(ln.unit_price)}</td>
        <td class="num">${money(ln.line_total)}</td>
      </tr>`,
    )
    .join('');
  const footerHtml = (r.footer_lines || [])
    .map((ln) => `<div class="center small">${esc(ln)}</div>`)
    .join('');

  return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="utf-8" />
  <title>Ticket ${esc(r.order_number)}</title>
  <style>
    @page { size: 80mm auto; margin: 4mm; }
    * { box-sizing: border-box; }
    body {
      font-family: "Courier New", Courier, monospace;
      font-size: 11px;
      line-height: 1.25;
      margin: 0;
      padding: 4px;
      width: 72mm;
      color: #000;
    }
    .center { text-align: center; }
    .bold { font-weight: bold; }
    .small { font-size: 9px; }
    .logo { max-width: 64mm; max-height: 28mm; margin: 0 auto 6px; display: block; }
    .sep { border: none; border-top: 1px dashed #000; margin: 6px 0; }
    .sep-dbl { border: none; border-top: 2px solid #000; margin: 6px 0; }
    table { width: 100%; border-collapse: collapse; }
    td { vertical-align: top; padding: 2px 0; }
    td.desc { width: 42%; word-break: break-word; }
    td.num { text-align: right; white-space: nowrap; }
    .meta { margin: 2px 0; }
    .total { font-size: 13px; font-weight: bold; text-align: right; margin: 8px 0; }
    .amount-words { font-size: 9px; margin: 4px 0 8px; text-align: center; }
    .section-title { text-align: center; font-weight: bold; margin: 6px 0; }
  </style>
</head>
<body>
  ${imgUrl ? `<img class="logo" src="${esc(imgUrl)}" alt="" />` : ''}
  ${headerHtml}
  <hr class="sep" />
  <div class="meta">Fecha: ${esc(r.datetime_label)}</div>
  <div class="meta">${esc(r.ticket_label)}</div>
  <div class="meta">Cajero: ${esc(r.cashier_label)}</div>
  <div class="meta">Caja: ${esc(r.register_label)}</div>
  <div class="meta">Cliente: ${esc(r.customer_label)}</div>
  <div class="meta">Vendedor: ${esc(r.seller_label)}</div>
  <hr class="sep" />
  <div class="section-title">- DETALLE DE SU COMPRA -</div>
  <table>
    <thead>
      <tr>
        <td class="desc bold">DESCRIPCIÓN</td>
        <td class="num bold">CANT</td>
        <td class="num bold">PRECIO</td>
        <td class="num bold">IMPORTE</td>
      </tr>
    </thead>
    <tbody>${linesHtml || '<tr><td colspan="4" class="center">Sin líneas</td></tr>'}</tbody>
  </table>
  <hr class="sep-dbl" />
  ${r.delivery_fee > 0 ? `<div class="meta" style="text-align:right">Envío: ${money(r.delivery_fee)}</div>` : ''}
  ${r.promo_discount > 0 ? `<div class="meta" style="text-align:right">Descuento: -${money(r.promo_discount)}</div>` : ''}
  <div class="total">TOTAL: ${money(r.total)}</div>
  <div class="amount-words">IMPORTE CON LETRA: ${esc(r.amount_words)}</div>
  <hr class="sep" />
  <div class="section-title">- FORMA DE PAGO -</div>
  <div class="meta">${esc(r.payment_method_label)}: ${money(r.total)}</div>
  <hr class="sep" />
  ${footerHtml}
</body>
</html>`;
}

function getPrintFrame() {
  let iframe = document.getElementById(PRINT_FRAME_ID);
  if (!iframe) {
    iframe = document.createElement('iframe');
    iframe.id = PRINT_FRAME_ID;
    iframe.setAttribute('title', 'Ticket de venta');
    iframe.style.cssText =
      'position:fixed;left:-10000px;top:0;width:80mm;height:200mm;border:0;opacity:0;pointer-events:none;';
    document.body.appendChild(iframe);
  }
  return iframe;
}

function printViaIframe(html) {
  const iframe = getPrintFrame();
  const win = iframe.contentWindow;
  const doc = win?.document;
  if (!win || !doc) {
    throw new Error('No se pudo preparar la ventana de impresión');
  }
  doc.open();
  doc.write(html);
  doc.close();

  const triggerPrint = () => {
    try {
      win.focus();
      win.print();
    } catch {
      throw new Error('No se pudo abrir el diálogo de impresión');
    }
  };

  if (doc.readyState === 'complete') {
    window.setTimeout(triggerPrint, 200);
  } else {
    iframe.onload = () => window.setTimeout(triggerPrint, 200);
  }
}

function printViaPopup(html) {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, '_blank', 'width=420,height=720');
  if (!w) {
    URL.revokeObjectURL(url);
    throw new Error('Permite ventanas emergentes para imprimir el ticket');
  }
  const cleanup = () => URL.revokeObjectURL(url);
  w.addEventListener('load', () => {
    cleanup();
    w.focus();
    w.print();
  }, { once: true });
}

/**
 * Genera HTML de ticket térmico (~80mm) e imprime.
 * @param {{ receipt: object }} payload — respuesta de GET /admin/orders/:id/receipt
 */
export function printOrderReceipt(payload) {
  const r = payload?.receipt;
  if (!r || typeof r !== 'object') {
    throw new Error('El ticket no tiene datos para imprimir');
  }
  const html = buildReceiptHtml(r);
  try {
    printViaIframe(html);
  } catch {
    printViaPopup(html);
  }
}
